const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const isUuid = (value) => typeof value === "string" && UUID_RE.test(value);

export const extractInviteToken = (rawValue) => {
  if (!rawValue) return "";
  const value = String(rawValue).trim();
  if (!value) return "";
  if (!value.includes("://")) return value;

  try {
    const url = new URL(value);
    const fromQuery = url.searchParams.get("map_invite_token")
      || url.searchParams.get("invite_token")
      || url.searchParams.get("token");
    if (fromQuery) return fromQuery.trim();

    const parts = url.pathname.split("/").filter(Boolean);
    if (parts.length > 0) return parts[parts.length - 1].trim();
  } catch {
    return value;
  }
  return value;
};

const parseJsonMaybe = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }
  return null;
};

const normalizePoint = (value) => {
  const point = parseJsonMaybe(value);
  if (!point || point.type !== "Point" || !Array.isArray(point.coordinates) || point.coordinates.length < 2) {
    return null;
  }
  return {
    type: "Point",
    coordinates: [Number(point.coordinates[0]), Number(point.coordinates[1])],
  };
};

export const normalizeOsmId = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") {
    return Number.isInteger(value) ? value : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (!/^-?\d+$/.test(trimmed)) return null;
    const parsed = Number(trimmed);
    return Number.isSafeInteger(parsed) ? parsed : null;
  }
  return null;
};

const getBoundsFromPolygon = (polygon) => {
  if (!polygon || polygon.type !== "Polygon" || !Array.isArray(polygon.coordinates)) return null;
  const ring = polygon.coordinates[0];
  if (!Array.isArray(ring) || ring.length === 0) return null;

  let minLon = Number.POSITIVE_INFINITY;
  let minLat = Number.POSITIVE_INFINITY;
  let maxLon = Number.NEGATIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;

  ring.forEach((coord) => {
    if (!Array.isArray(coord) || coord.length < 2) return;
    const lon = Number(coord[0]);
    const lat = Number(coord[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    minLon = Math.min(minLon, lon);
    minLat = Math.min(minLat, lat);
    maxLon = Math.max(maxLon, lon);
    maxLat = Math.max(maxLat, lat);
  });

  if (!Number.isFinite(minLon) || !Number.isFinite(minLat) || !Number.isFinite(maxLon) || !Number.isFinite(maxLat)) {
    return null;
  }

  return {
    west: minLon,
    south: minLat,
    east: maxLon,
    north: maxLat,
  };
};

export const getCenterFromPolygon = (polygon, fallbackPoint = null) => {
  const bounds = getBoundsFromPolygon(polygon);
  if (bounds) {
    return {
      lon: (bounds.west + bounds.east) / 2,
      lat: (bounds.south + bounds.north) / 2,
      bounds,
    };
  }

  const point = normalizePoint(fallbackPoint);
  if (point) {
    return {
      lon: point.coordinates[0],
      lat: point.coordinates[1],
      bounds: null,
    };
  }

  return null;
};

export const toLegacyMapFeature = ({ mapRoot, revision, isCorporate = false }) => {
  if (!mapRoot || !revision) return null;

  const bbox = parseJsonMaybe(revision.bbox);
  const firstPoiCenter = revision.pois?.[0]?.center || revision.center || null;
  const centerInfo = getCenterFromPolygon(bbox, firstPoiCenter);
  const pois = (revision.pois || []).map((poi) => ({
    id: poi.id,
    center: normalizePoint(poi.center),
    number: poi.number,
    name: poi.name,
    osm_id: poi.osm_id,
    sort_order: poi.sort_order,
    metadata: poi.metadata || {},
    online_title: poi.online_title,
    online_text: poi.online_text,
    online_photo_key: poi.online_photo_key,
    online_photo_url: poi.online_photo_url || null,
    online_only_enrichment: poi.online_only_enrichment,
  })).filter((poi) => poi.center);

  return {
    type: "Feature",
    id: mapRoot.id,
    geometry: centerInfo ? [centerInfo.lon, centerInfo.lat] : null,
    properties: {
      area_name: revision.area_name || mapRoot.name || "",
      country_name: revision.country_name || "",
      boundaries: bbox || null,
      center_r: centerInfo ? [centerInfo.lat, centerInfo.lon] : null,
      ne_r: centerInfo?.bounds ? [centerInfo.bounds.north, centerInfo.bounds.east] : null,
      sw_r: centerInfo?.bounds ? [centerInfo.bounds.south, centerInfo.bounds.west] : null,
      mappoints: pois,
      map_user: mapRoot.owner_id,
      min_zoom: revision.min_zoom,
      max_zoom: revision.max_zoom,
      show_zoom: revision.show_zoom,
      show_amenities: revision.show_amenities,
      visibility_settings: revision.visibility_settings || {},
      is_visible_to_customers: revision.is_visible_to_customers,
      customer_visibility_notes: revision.customer_visibility_notes,
      file_path: revision.file_path,
      file_name: revision.file_name,
      pdf_path: revision.pdf_path,
      pdf_path_no_qr: revision.pdf_path_no_qr,
      pdf_vector_path: revision.pdf_vector_path,
      pdf_vector_path_no_qr: revision.pdf_vector_path_no_qr,
      pdf_vector_unavailable_reason: revision.pdf_vector_unavailable_reason,
      jpg_125dpi_q85_path: revision.jpg_125dpi_q85_path,
      jpg_125dpi_q85_path_no_qr: revision.jpg_125dpi_q85_path_no_qr,
      jpg_200dpi_q90_path: revision.jpg_200dpi_q90_path,
      jpg_200dpi_q90_path_no_qr: revision.jpg_200dpi_q90_path_no_qr,
      jpg_additional_variants: revision.jpg_additional_variants || {},
      tile_count: revision.tile_count,
      map_size: revision.map_size,
      to_refresh: revision.to_refresh,
      api_key: revision.api_key,
      offline_package_path: revision.offline_package_path,
      offline_package_version: revision.offline_package_version,
      offline_package_size: revision.offline_package_size,
      offline_package_format: revision.offline_package_format,
      offline_package_updated_at: revision.offline_package_updated_at,
      owner_tags: Array.isArray(mapRoot.owner_tags) ? mapRoot.owner_tags : [],
      my_tags: Array.isArray(mapRoot.my_tags) ? mapRoot.my_tags : [],
      ports_of_call: Array.isArray(mapRoot.ports_of_call)
        ? mapRoot.ports_of_call
        : (Array.isArray(revision.ports_of_call) ? revision.ports_of_call : []),
    },
    is_new: false,
    is_corporate: Boolean(isCorporate || revision.is_corporate),
    is_versioned: true,
    can_edit: Boolean(mapRoot.can_edit),
    current_revision_id: revision.id,
    revision_no: revision.revision_no,
    revision_visibility: revision.visibility,
    has_full_details: true,
  };
};

export const toLegacyMapFeatureFromList = ({ mapRoot }) => {
  if (!mapRoot) return null;
  const revision = mapRoot.current_revision || null;
  if (!revision) {
    return {
      type: "Feature",
      id: mapRoot.id,
      geometry: null,
      properties: {
        area_name: mapRoot.name || "",
        country_name: "",
        boundaries: null,
        center_r: null,
        ne_r: null,
        sw_r: null,
        mappoints: [],
        map_user: mapRoot.owner_id,
        visibility_settings: {},
        owner_tags: Array.isArray(mapRoot.owner_tags) ? mapRoot.owner_tags : [],
        my_tags: Array.isArray(mapRoot.my_tags) ? mapRoot.my_tags : [],
        ports_of_call: Array.isArray(mapRoot.ports_of_call) ? mapRoot.ports_of_call : [],
      },
      is_new: false,
      is_versioned: true,
      can_edit: Boolean(mapRoot.can_edit),
      current_revision_id: mapRoot.current_revision_id || null,
      has_full_details: false,
    };
  }

  return {
    ...toLegacyMapFeature({
      mapRoot,
      revision: {
        ...revision,
        pois: [],
      },
      isCorporate: revision.is_corporate,
    }),
    can_edit: Boolean(mapRoot.can_edit),
    current_revision_id: mapRoot.current_revision_id || revision.id || null,
    has_full_details: false,
  };
};

export const toLegacyMapFeatureFromLite = ({ mapRoot }) => {
  if (!mapRoot) return null;

  const center = normalizePoint(mapRoot.center);

  return {
    type: "Feature",
    id: mapRoot.id,
    geometry: center ? [center.coordinates[0], center.coordinates[1]] : null,
    properties: {
      area_name: mapRoot.area_name || mapRoot.name || "",
      country_name: mapRoot.country_name || "",
      boundaries: null,
      center_r: center ? [center.coordinates[1], center.coordinates[0]] : null,
      ne_r: null,
      sw_r: null,
      mappoints: [],
      map_user: mapRoot.owner_id,
      visibility_settings: {},
      owner_tags: Array.isArray(mapRoot.owner_tags) ? mapRoot.owner_tags : [],
      my_tags: Array.isArray(mapRoot.my_tags) ? mapRoot.my_tags : [],
      api_key: mapRoot.api_key || null,
    },
    is_new: false,
    is_versioned: true,
    can_edit: Boolean(mapRoot.can_edit),
    current_revision_id: mapRoot.current_revision_id || null,
    revision_no: mapRoot.revision_no ?? null,
    has_full_details: false,
  };
};

export const buildRevisionPayload = ({
  sourceRevisionId,
  message,
  bbox,
  mapName,
  countryName,
  minZoom,
  maxZoom,
  showZoom,
  showAmenities,
  visibilitySettings,
  isVisibleToCustomers,
  customerVisibilityNotes,
  pois,
  tourUpdatePolicy = "none",
  includeManualPinLocked = false,
  portOfCallIds = null,
  offlinePackagePath = null,
  offlinePackageVersion = null,
  offlinePackageSize = null,
  offlinePackageFormat = null,
  offlinePackageUpdatedAt = null,
  pdfVectorPath = null,
  pdfVectorPathNoQr = null,
  pdfVectorUnavailableReason = null,
  jpg125dpiQ85Path = null,
  jpg125dpiQ85PathNoQr = null,
  jpg200dpiQ90Path = null,
  jpg200dpiQ90PathNoQr = null,
  jpgAdditionalVariants = null,
}) => {
  const payload = {
    message: message || "",
    area_name: mapName || "",
    country_name: countryName || "",
    min_zoom: minZoom,
    max_zoom: maxZoom,
    show_zoom: showZoom,
    show_amenities: Boolean(showAmenities),
    visibility_settings: visibilitySettings || {},
    is_visible_to_customers: isVisibleToCustomers,
    customer_visibility_notes: customerVisibilityNotes || "",
    tour_update_policy: tourUpdatePolicy,
    include_manual_pin_locked: Boolean(includeManualPinLocked),
    pois: (pois || []).map((poi, index) => ({
      center: poi.center,
      number: Number.isFinite(poi.number) ? poi.number : index + 1,
      name: poi.name || "",
      osm_id: normalizeOsmId(poi.osm_id),
      sort_order: Number.isFinite(poi.sort_order) ? poi.sort_order : index,
      metadata: poi.metadata || {},
      online_title: poi.online_title || null,
      online_text: poi.online_text || null,
      online_photo_key: poi.online_photo_key || null,
      online_only_enrichment: poi.online_only_enrichment ?? true,
    })),
  };

  if (sourceRevisionId) payload.source_revision_id = sourceRevisionId;
  if (bbox) payload.bbox = bbox;
  if (Array.isArray(portOfCallIds)) payload.port_of_call_ids = portOfCallIds;
  if (offlinePackagePath !== null) payload.offline_package_path = offlinePackagePath;
  if (offlinePackageVersion !== null) payload.offline_package_version = offlinePackageVersion;
  if (offlinePackageSize !== null) payload.offline_package_size = offlinePackageSize;
  if (offlinePackageFormat !== null) payload.offline_package_format = offlinePackageFormat;
  if (offlinePackageUpdatedAt !== null) payload.offline_package_updated_at = offlinePackageUpdatedAt;
  if (pdfVectorPath !== null) payload.pdf_vector_path = pdfVectorPath;
  if (pdfVectorPathNoQr !== null) payload.pdf_vector_path_no_qr = pdfVectorPathNoQr;
  if (pdfVectorUnavailableReason !== null) payload.pdf_vector_unavailable_reason = pdfVectorUnavailableReason;
  if (jpg125dpiQ85Path !== null) payload.jpg_125dpi_q85_path = jpg125dpiQ85Path;
  if (jpg125dpiQ85PathNoQr !== null) payload.jpg_125dpi_q85_path_no_qr = jpg125dpiQ85PathNoQr;
  if (jpg200dpiQ90Path !== null) payload.jpg_200dpi_q90_path = jpg200dpiQ90Path;
  if (jpg200dpiQ90PathNoQr !== null) payload.jpg_200dpi_q90_path_no_qr = jpg200dpiQ90PathNoQr;
  if (jpgAdditionalVariants !== null) payload.jpg_additional_variants = jpgAdditionalVariants;
  return payload;
};

export const buildCreateMapRootPayload = ({
  mapName,
  countryName,
  bbox,
  minZoom,
  maxZoom,
  showZoom,
  showAmenities,
  visibilitySettings,
  isVisibleToCustomers,
  customerVisibilityNotes,
  pois,
  ownerTags = [],
  myTags = [],
  message = "",
  filePath = null,
  fileName = null,
  pdfPath = null,
  pdfPathNoQr = null,
  tileCount = null,
  mapSize = null,
  toRefresh = null,
  offlinePackagePath = null,
  offlinePackageVersion = null,
  offlinePackageSize = null,
  offlinePackageFormat = null,
  offlinePackageUpdatedAt = null,
  pdfVectorPath = null,
  pdfVectorPathNoQr = null,
  pdfVectorUnavailableReason = null,
  jpg125dpiQ85Path = null,
  jpg125dpiQ85PathNoQr = null,
  jpg200dpiQ90Path = null,
  jpg200dpiQ90PathNoQr = null,
  jpgAdditionalVariants = null,
  portOfCallIds = null,
}) => {
  const payload = {
    name: mapName || "",
    area_name: mapName || "",
    country_name: countryName || "",
    min_zoom: minZoom,
    max_zoom: maxZoom,
    show_zoom: showZoom,
    show_amenities: Boolean(showAmenities),
    visibility_settings: visibilitySettings || {},
    is_visible_to_customers: isVisibleToCustomers,
    customer_visibility_notes: customerVisibilityNotes || "",
    owner_tags: Array.isArray(ownerTags) ? ownerTags : [],
    my_tags: Array.isArray(myTags) ? myTags : [],
    message: message || "",
    pois: (pois || []).map((poi, index) => ({
      center: poi.center,
      number: Number.isFinite(poi.number) ? poi.number : index + 1,
      name: poi.name || "",
      osm_id: normalizeOsmId(poi.osm_id),
      sort_order: Number.isFinite(poi.sort_order) ? poi.sort_order : index,
      metadata: poi.metadata || {},
      online_title: poi.online_title || null,
      online_text: poi.online_text || null,
      online_photo_key: poi.online_photo_key || null,
      online_only_enrichment: poi.online_only_enrichment ?? true,
    })),
  };

  if (bbox) payload.bbox = bbox;
  if (filePath !== null) payload.file_path = filePath;
  if (fileName !== null) payload.file_name = fileName;
  if (pdfPath !== null) payload.pdf_path = pdfPath;
  if (pdfPathNoQr !== null) payload.pdf_path_no_qr = pdfPathNoQr;
  if (pdfVectorPath !== null) payload.pdf_vector_path = pdfVectorPath;
  if (pdfVectorPathNoQr !== null) payload.pdf_vector_path_no_qr = pdfVectorPathNoQr;
  if (pdfVectorUnavailableReason !== null) payload.pdf_vector_unavailable_reason = pdfVectorUnavailableReason;
  if (jpg125dpiQ85Path !== null) payload.jpg_125dpi_q85_path = jpg125dpiQ85Path;
  if (jpg125dpiQ85PathNoQr !== null) payload.jpg_125dpi_q85_path_no_qr = jpg125dpiQ85PathNoQr;
  if (jpg200dpiQ90Path !== null) payload.jpg_200dpi_q90_path = jpg200dpiQ90Path;
  if (jpg200dpiQ90PathNoQr !== null) payload.jpg_200dpi_q90_path_no_qr = jpg200dpiQ90PathNoQr;
  if (jpgAdditionalVariants !== null) payload.jpg_additional_variants = jpgAdditionalVariants;
  if (tileCount !== null) payload.tile_count = tileCount;
  if (mapSize !== null) payload.map_size = mapSize;
  if (toRefresh !== null) payload.to_refresh = toRefresh;
  if (offlinePackagePath !== null) payload.offline_package_path = offlinePackagePath;
  if (offlinePackageVersion !== null) payload.offline_package_version = offlinePackageVersion;
  if (offlinePackageSize !== null) payload.offline_package_size = offlinePackageSize;
  if (offlinePackageFormat !== null) payload.offline_package_format = offlinePackageFormat;
  if (offlinePackageUpdatedAt !== null) payload.offline_package_updated_at = offlinePackageUpdatedAt;
  if (Array.isArray(portOfCallIds)) payload.port_of_call_ids = portOfCallIds;

  return payload;
};
